import { Button, Group, Paper, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useBillingCheckout } from '@hooks/useBillingCheckout';
import classes from './trial-banner.module.css';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

interface TrialAccessBannerProps {
  trialEndsAt?: string | null;
}

function getTrialDaysLeft(trialEndsAt?: string | null): number | null {
  if (!trialEndsAt) return null;

  const trialEndMs = new Date(trialEndsAt).getTime();
  if (Number.isNaN(trialEndMs)) return null;

  return Math.max(0, Math.ceil((trialEndMs - Date.now()) / MS_PER_DAY));
}

function getTrialMessage(daysLeft?: number | null): string {
  if (typeof daysLeft !== 'number') {
    return 'You are on a free trial. Subscribe to a paid plan to keep uninterrupted access when it ends.';
  }

  if (daysLeft <= 0) {
    return 'Your trial ends today — subscribe now to a paid plan to keep your access.';
  }

  const dayLabel = daysLeft === 1 ? 'day' : 'days';
  return `Your trial ends in ${daysLeft} ${dayLabel}. Subscribe early to a paid plan to keep uninterrupted access.`;
}

function TrialAccessBanner({ trialEndsAt = null }: TrialAccessBannerProps) {
  const navigate = useNavigate();
  const daysLeft = getTrialDaysLeft(trialEndsAt);
  const { startBillingCheckout, isLoading } = useBillingCheckout();

  const handleStartSubscription = async () => {
    await startBillingCheckout({
      onMissingPriceId: () => navigate('/billing'),
      onMissingRedirectUrl: () => navigate('/billing'),
      onError: () => navigate('/billing'),
    });
  };

  return (
    <Paper withBorder radius="md" p="md" mb="md" className={classes.trialBar}>
      <Group justify="space-between" align="center" className={classes.trialBarInner}>
        <div className={classes.trialCopy}>
          <Text
            size="xs"
            fw={700}
            className={classes.trialEyebrow}
            style={{ color: 'var(--mantine-color-voySand-1)', letterSpacing: '0.08em' }}
          >
            TRIAL ACCESS
          </Text>
          <Text size="sm" fw={600} c="white">
            {getTrialMessage(daysLeft)}
          </Text>
        </div>
        <Button
          size="sm"
          color="voyOrange"
          className={classes.trialCta}
          onClick={handleStartSubscription}
          loading={isLoading}
        >
          Upgrade to a paid plan
        </Button>
      </Group>
    </Paper>
  );
}

export default TrialAccessBanner;
